import { getClaimListingLink } from '@pebble/common';
import {
  Box,
  Grid,
  Group,
  Title,
  Text,
  Card,
  Container,
  Spoiler,
  Image,
  Flex,
  ActionIcon,
  Modal,
  useMantineTheme,
} from '@mantine/core';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { ChildFace } from '@icons';
import buildFullAddressFromLocation, {
  AddressFieldsType,
} from 'utils/buildFullAddressFromLocation';
import { ActivityDetails as ActivityDetailsType } from 'types';
import { Supplier } from 'interfaces';
import { ActivityTypeEnum } from 'enums';
import ActivityMap from 'components/ActivityMap/ActivityMap';
import BackButton from 'components/BackButton/BackButton';
import { CloudinaryImage } from 'components/ui';
import classes from './ActivityDetails.module.scss';
import Link from 'next/link';
import { trackAction, Actions } from 'utils/amplitude';
import { useDisclosure, useMediaQuery, useScrollIntoView, useSessionStorage } from '@mantine/hooks';
import ShareButton from 'components/ui/ShareButton/ShareButton';
import { supplierDetailsExist } from 'components/ActivityDetails/ActivityDetails.utils';
import SubscriptionInfo from 'components/SubscriptionInfo/SubscriptionInfo';
import { VerifiedButton, VerifiedModal } from 'components/VerifiedModal';
import { EmbedContext, EmbedContextType } from 'context/EmbedContext';
import SocialLinksBanner from 'components/SocialLinksBanner/SocialLinksBanner';
import CategoryIcon from 'components/ui/CategoryIcon/CategoryIcon';
import { useBasket } from 'context/BasketContext';
import EmbedErrorComponent from 'components/EmbedIncognitoError/EmbedIncognitoError';
import LeavingModalEmbed from './LeavingModal/LeavingModalEmbed';
import SessionSelectEmbed from '../SessionSelect/SessionSelectEmbed';
import ImageCarousel from 'components/ActivityDetails/ImageCarousel/ImageCarousel';
import classNames from 'classnames';
import { MapPin, VideoCamera } from '@phosphor-icons/react';

interface ActivityDetailsProps {
  activityDetails: ActivityDetailsType;
  goBack: () => void;
  redirectToCheckout: (basketId: string) => void;
  activityId: string | undefined;
  userToken: string | null;
  hasReviews: boolean;
  isChromeIncognito?: boolean;
}

export interface UtmParamsData {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
}

const addressFields: AddressFieldsType = ['addressLine1', 'addressLine2', 'city', 'postCode'];

const getSupplierNameJSX = (supplier: Supplier) => {
  if (supplierDetailsExist(supplier)) {
    return (
      <Link
        href={`/supplier/${supplier.slug}`}
        data-cy="supplierLink"
        onClick={() => {
          trackAction(Actions.SUPPLIER_SELECTED_DETAILS);
        }}
      >
        {supplier?.name}
      </Link>
    );
  }
  return supplier.name;
};

export const GetUtmParams = ({
  setUtmParams,
  isChromeIncognito,
}: {
  setUtmParams: Dispatch<SetStateAction<UtmParamsData>>;
  isChromeIncognito: boolean;
}) => {
  const [utmParams] = useSessionStorage<UtmParamsData>({
    key: 'pebble_utm_params',
  });

  useEffect(() => {
    if (isChromeIncognito) return;

    setUtmParams(utmParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChromeIncognito, utmParams]);

  return <></>;
};

const ActivityDetailsEmbed: React.FC<ActivityDetailsProps> = (props) => {
  const { activityDetails, goBack, userToken, isChromeIncognito } = props;

  const { activity } = activityDetails;

  const { supplier } = activity;

  const { iframeLocation } = useContext(EmbedContext) as EmbedContextType;

  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 768px)', true);
  const { scrollIntoView: scrollToSessionSelect, targetRef: sessionSelectRef } =
    useScrollIntoView<HTMLDivElement>({
      duration: 750,
      offset: 96,
    });

  const { scrollIntoView: scrollToMap, targetRef: mapRef } = useScrollIntoView<HTMLDivElement>({
    duration: 750,
    offset: 96,
  });

  const { setActivityDetails, setUserToken, basketItems, selectedSession, setSelectedSession } =
    useBasket();

  const [showVerifiedModal, setShowVerifiedModal] = useState<boolean>(false);

  const [blockBookingTabActive, setBlockBookingTabActive] = useState<boolean>(
    activity.allowBlockBookings,
  );

  const [userSelectedRadio, setUserSelectedRadio] = useState(false);

  const [utmParams, setUtmParams] = useState<UtmParamsData>({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
  });
  const [opened, { close }] = useDisclosure(true);

  const [openLeavingModal, setOpenLeavingModal] = useState(false);

  const getSingleImage = () => {
    if (activity.cloudinaryImageId) {
      return (
        <section className={classes.cardHeader} aria-label="activity header">
          <div className={classes.imageWrapper}>
            <CloudinaryImage
              id={activity?.cloudinaryImageId}
              alt={`${activity.name} - ${supplier.name}`}
              isActivityDetails={true}
            />
          </div>
        </section>
      );
    } else {
      return (
        <Image
          src={
            isMobile
              ? '../../pebble_placeholder_image.png'
              : '../../../../pebble_placeholder_activity_details.png'
          }
          width="100%"
          height="100%"
          fit="contain"
          alt="Patterns with shapes and lines"
        />
      );
    }
  };

  //   Update basket context with activity details
  useEffect(() => {
    if (userToken) setUserToken(userToken);

    setActivityDetails(activityDetails);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityDetails, userToken]);

  if (isChromeIncognito) {
    return (
      <Modal
        opened={opened}
        onClose={close}
        fullScreen
        withCloseButton={false}
        style={{
          '.mantine-Modal-body': {
            height: '100%',
          },
        }}
      >
        <EmbedErrorComponent closeButton={close} />;
      </Modal>
    );
  }

  return (
    <>
      <Container mt="lg" className={classes.container}>
        <GetUtmParams setUtmParams={setUtmParams} isChromeIncognito={isChromeIncognito || false} />
        <Grid gutter="md" grow columns={12}>
          <Grid.Col span={12}>
            {/** The onClick is a callback because useRouter is undefined in test env and it's a pain to mock. */}
            <BackButton
              onClick={() => {
                trackAction(Actions.ACTIVITY_BACK_IFRAME, {
                  iframeLocation,
                });

                if (userSelectedRadio || basketItems.length > 0) {
                  setOpenLeavingModal(true);
                } else {
                  goBack();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={12} maw={'100%'}>
            <section className={classes.cardHeader} aria-label="activity header">
              {activity.additionalImageIds?.length > 0 && activity.cloudinaryImageId ? (
                <ImageCarousel
                  allImageIds={[activity.cloudinaryImageId, ...activity.additionalImageIds]}
                  activityName={activity.name}
                  supplierName={supplier.name}
                  isActivityDetails
                  trackEvent={() => trackAction(Actions.ADP_CAROUSEL_IFRAME, { iframeLocation })}
                />
              ) : (
                getSingleImage()
              )}
            </section>
          </Grid.Col>
          <Grid.Col span={8} pb="0">
            <Box
              component="section"
              className={classes.activityHeader}
              aria-label="activity header"
            >
              <Title mb="xs" order={1}>
                {activity?.seoPageTitle}
              </Title>
              <Flex direction="row" align="baseline" wrap={isMobile ? 'wrap' : 'nowrap'}>
                <Title order={2} className={classes.supplierDetailsLink}>
                  {getSupplierNameJSX(supplier)}
                </Title>
              </Flex>
              {isMobile && (
                <VerifiedButton onClick={() => setShowVerifiedModal(true)} activityDetailsPage />
              )}
            </Box>
          </Grid.Col>
          <Grid.Col span={4} pt="sm">
            <Flex direction="column" align="flex-end">
              <ShareButton activityName={activity?.name} />
              {!isMobile && (
                <VerifiedButton onClick={() => setShowVerifiedModal(true)} activityDetailsPage />
              )}
            </Flex>
          </Grid.Col>
          <Grid.Col span={12} className={classes.pageSection} py="0">
            <Box component="section" aria-label="activity location">
              <Group gap={0} wrap={'nowrap'} onClick={() => scrollToMap({ alignment: 'start' })}>
                {activity.isOnline ? (
                  <Flex direction="row" align="center">
                    <ActionIcon title="online video icon" variant="transparent">
                      <VideoCamera
                        className={classes.onlineIcon}
                        color={theme.colors.blue[8]}
                        weight="fill"
                      />
                    </ActionIcon>
                    <Text className={classes.onlineBadge}>ONLINE</Text>
                  </Flex>
                ) : (
                  <Flex
                    align="center"
                    pt={6}
                    onClick={() => {
                      trackAction(Actions.ACTIVITY_ADDRESS_IFRAME, {
                        iframeLocation,
                      });
                    }}
                  >
                    <ActionIcon
                      title="location pin icon"
                      size={'sm'}
                      style={{ justifyContent: 'center' }}
                      variant="transparent"
                    >
                      <MapPin className={classes.locationPin} size={18} weight="fill" />
                    </ActionIcon>

                    <Text className={classNames(classes.activityDetailsLink, classes.locationLink)}>
                      {activity.locationName && activity.locationName + ', '}
                      {buildFullAddressFromLocation(addressFields, activity.location)}
                    </Text>
                  </Flex>
                )}
              </Group>
            </Box>
          </Grid.Col>
          <Grid.Col span={12} className={classes.pageSection} pt="0" mt={isMobile ? 0 : 6}>
            <Box component="section" aria-label="about">
              <Title order={3} className={classes.sectionHeader}>
                About {activity.name}
              </Title>

              <Spoiler
                maxHeight={208}
                showLabel="Read more"
                hideLabel="Read less"
                classNames={{
                  control: classes.spoilerControl,
                }}
                onClick={() => {
                  trackAction(Actions.READ_MORE_DETAILS);
                }}
              >
                <Box className={classes.sectionText}>
                  <div dangerouslySetInnerHTML={{ __html: activity.description }} />
                </Box>
              </Spoiler>
            </Box>
          </Grid.Col>

          {activity.ageRange && (
            <Grid.Col span={12} className={classes.pageSection}>
              <Group className={classes.iconsContainer}>
                <Flex direction="row" align="center">
                  <ActionIcon title="age range icon" mr={8} variant="subtle">
                    <ChildFace />
                  </ActionIcon>
                  <Text size="sm" color={theme.colors.gray[6]}>
                    {activity.ageRange}
                  </Text>
                </Flex>
                {activity.categories?.length > 0 &&
                  activity.categories?.map((category) => {
                    return (
                      <CategoryIcon
                        data-cy="category-badge"
                        category={category}
                        key={category.id}
                      />
                    );
                  })}
              </Group>
            </Grid.Col>
          )}

          {activity.isOnline && isMobile && (
            <Grid.Col span={7} className={classes.pageSection} mb="lg" mt="sm">
              <Title order={3} className={classes.sectionHeader}>
                Online call details
              </Title>
              <Text className={classes.sectionText}>{activity.onlinePublicInfo}</Text>
            </Grid.Col>
          )}

          {activity.activityType === ActivityTypeEnum.SUBSCRIPTION && activity.subscriptionInfo && (
            <Grid.Col span={12} px={0} pt="sm">
              <SubscriptionInfo info={activity.subscriptionInfo} />
            </Grid.Col>
          )}

          <Grid.Col span={isMobile ? 12 : 7} className={classes.pageSection}>
            {activity.isOnline && !isMobile && (
              <Box component="section" aria-label="online call details">
                <Title order={3} className={classes.sectionHeader}>
                  Online call details
                </Title>
                <Text className={classes.sectionText}>{activity.onlinePublicInfo}</Text>
              </Box>
            )}

            <Box hidden={!activity?.guardianToProvide || isMobile} mb="md">
              <Title order={3} className={classes.sectionHeader}>
                What you need to bring
              </Title>
              <Text className={classes.sectionText}>{activity.guardianToProvide}</Text>
            </Box>

            <Box hidden={!activity.supplierToProvide || isMobile} mb="md">
              <Title order={3} className={classes.sectionHeader}>
                What we provide
              </Title>
              <Text className={classes.sectionText}>{activity.supplierToProvide}</Text>
            </Box>
          </Grid.Col>
          <div ref={sessionSelectRef}>
            <Grid.Col span={5} className={classes.pageSection}>
              <SessionSelectEmbed
                activityDetails={activityDetails}
                selectedSession={selectedSession}
                setSelectedSession={setSelectedSession}
                setUserSelectedRadio={setUserSelectedRadio}
                blockBookingTabActive={blockBookingTabActive}
                setBlockBookingTabActive={setBlockBookingTabActive}
                utmParams={utmParams}
              />
            </Grid.Col>
          </div>
          <Grid.Col
            span={12}
            hidden={!activity?.guardianToProvide || !isMobile}
            className={classes.pageSection}
          >
            <Box component="section" aria-label="pricing" mb="md">
              <Title order={3} className={classes.sectionHeader}>
                What you need to bring
              </Title>
              <Text className={classes.sectionText}>{activity.guardianToProvide}</Text>
            </Box>
          </Grid.Col>
          <Grid.Col
            span={12}
            hidden={!activity.supplierToProvide || !isMobile}
            className={classes.pageSection}
          >
            <Box component="section" aria-label="pricing" mb="md">
              <Title order={3} className={classes.sectionHeader}>
                What we provide
              </Title>
              <Text className={classes.sectionText}>{activity.supplierToProvide}</Text>
            </Box>
          </Grid.Col>
          {!activity.isOnline && (
            <Grid.Col span={12}>
              <Card
                component="section"
                className={classes.cardContainer}
                aria-label="activity location map"
                ref={mapRef}
              >
                <Title className={classes.mapHeader} order={3} mt={0}>
                  {activity.locationName && (
                    <span data-testid="map header building">{`${activity.locationName}, `}</span>
                  )}
                  {activity?.location?.addressLine1}
                </Title>
                <Box className={classes.mapWrapper}>
                  <ActivityMap activity={activity} />
                </Box>
              </Card>
            </Grid.Col>
          )}
          <Grid.Col span={12} className={classes.pageSection} aria-label="supplier details">
            <SocialLinksBanner
              supplierName={supplier.name}
              facebookLink={supplier.facebook}
              instagramLink={supplier.instagram}
              twitterLink={supplier.twitter}
              trackEvent={() =>
                trackAction(Actions.ADP_SOCIALS_IFRAME, {
                  iframeLocation,
                })
              }
            />
            {!activity?.supplier?.isClaimed && (
              <Text className={classes.sectionText} mt="md">
                Are you the supplier for this activity? <br />
                Claim your activity listing{' '}
                <a href={getClaimListingLink(activity.supplier)}>here</a>.
              </Text>
            )}
          </Grid.Col>
        </Grid>
        <VerifiedModal
          data-cy="modal"
          opened={showVerifiedModal}
          onClose={() => setShowVerifiedModal(false)}
        />
      </Container>

      {openLeavingModal && (
        <LeavingModalEmbed
          opened={openLeavingModal}
          onClose={() => setOpenLeavingModal(!openLeavingModal)}
          goBack={() => goBack()}
          onClick={() => scrollToSessionSelect()}
        />
      )}
    </>
  );
};

export default ActivityDetailsEmbed;
